import { useDeleteCommodityTypeMutation } from "@/pages/sy/hooks/queries/useCommodityTypesQuery";
import {
  ErrorModal,
  HelmetDexteel,
  MesfModal,
  useContextMenuMESF,
} from "@dexteel/mesf-core";
import { Button, Grid, IconButton, Typography } from "@mui/material";
import { PlaylistAdd as PlaylistAddIcon } from "@mui/icons-material";
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  IStatusPanelParams,
  MenuItemDef,
  RowSelectedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CenteredLazyLoading } from "../../../../../controls/LazyLoading";
import { useGridDefinitions } from "../../../../../controls/ag-grid/components/GridDefinitions";
import { TextFieldSearchFilter } from "../../../../../shared/TextFieldSearchFilter";
import { CommodityType } from "../../../models/CommodityType";
import { useCommodityTypeOptionsFunctions } from "../hooks/useCommodityTypeOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { CommodityTypeDetails } from "./CommodityTypeDetails";

const BASE_PATH = "/configuration/scrap-yard/commodity-types";

const AddCommodityTypeStatusPanel = ({
  handleNewCommodityType,
}: {
  handleNewCommodityType: () => void;
}) => {
  return (
    <IconButton
      onClick={handleNewCommodityType}
      color="primary"
      style={{
        padding: 0,
        height: "100%",
        display: "flex",
        alignItems: "center",
      }}
    >
      <PlaylistAddIcon style={{ width: 30, height: 30 }} />
    </IconButton>
  );
};

type Props = {
  isLoading: boolean;
  commodityTypes: CommodityType[];
  commodityTypeId: string | undefined;
};

export const TableCommodityType = ({
  isLoading,
  commodityTypes,
  commodityTypeId,
}: Props) => {
  const navigate = useNavigate();

  const deleteCommodityTypeMutation = useDeleteCommodityTypeMutation();

  const [error, setError] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [showModal, setShowModal] = useState<"new" | "edit" | "delete" | "">(
    "",
  );
  const [commodityType, setCommodityType] = useState<CommodityType | null>(
    null,
  );
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [pendingSelectCommodityTypeId, setPendingSelectCommodityTypeId] =
    useState<number | null>(null);

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  useEffect(() => {
    if (pendingSelectCommodityTypeId && gridApi) {
      const rowNode = gridApi.getRowNode(String(pendingSelectCommodityTypeId));
      if (rowNode) {
        gridApi.ensureIndexVisible(rowNode.rowIndex ?? 0, "middle");
        rowNode.setSelected(true);
        setCommodityType(rowNode.data);
        setPendingSelectCommodityTypeId(null);
      }
    }
  }, [commodityTypes, pendingSelectCommodityTypeId, gridApi]);

  const handleNewCommodityType = () => {
    if (gridApi) gridApi.deselectAll();
    setShowModal("new");
    setCommodityType(null);
    navigate(`${BASE_PATH}/new`);
  };

  const { getMenuOptions } = useCommodityTypeOptionsFunctions({
    setCommodityType,
    setShowModal,
    handleNewCommodityType,
  });

  const { rows, columnDefs } = useTableData({
    commodityTypes,
    setCommodityType,
    setShowModal,
    showContextMenu,
  });

  const { columnSimpleDefaults } = useGridDefinitions({
    OnEdit: (data: CommodityType) => {
      setShowModal("edit");
      setCommodityType(data);
    },
  });

  const onRowSelected = (event: RowSelectedEvent) => {
    const cell = gridApi?.getFocusedCell();
    if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
      setCommodityType(event.data);
      setShowModal("edit");
      navigate(`${BASE_PATH}/${(event.data as CommodityType).CommodityTypeId}`);
    }
  };

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const data = params.node?.data;
      const nativeEvent = (
        params as GetContextMenuItemsParams & { event?: MouseEvent }
      ).event;

      if (data && nativeEvent) {
        showContextMenu(
          nativeEvent as unknown as React.MouseEvent,
          data,
          "commodityTypeContext",
        );
        params.api.deselectAll();
        params.node?.setSelected(true);
      }
      return [];
    },
    [rows],
  );

  const onModalHide = ({
    shouldUpdate,
    close,
    entityId,
  }: {
    shouldUpdate: boolean;
    close: boolean;
    entityId?: number;
  }) => {
    if (close) {
      setShowModal("");
      setCommodityType(null);
      if (gridApi) gridApi.deselectAll();
    }
    if (shouldUpdate && entityId) {
      const rowNode = gridApi?.getRowNode(String(entityId));
      if (rowNode && gridApi) {
        gridApi.ensureIndexVisible(rowNode.rowIndex ?? 0, "middle");
        rowNode.setSelected(true);
        setCommodityType(rowNode.data);
      } else {
        setPendingSelectCommodityTypeId(entityId);
      }
    }
    navigate(BASE_PATH);
  };

  useEffect(() => {
    if (commodityTypeId) {
      if (commodityTypeId === "new") {
        handleNewCommodityType();
      } else {
        let commodityTypeNode: IRowNode<CommodityType> | undefined;

        gridApi?.forEachNode((n) => {
          if (
            (n.data as CommodityType).CommodityTypeId ===
            Number(commodityTypeId)
          ) {
            commodityTypeNode = n;
            return true;
          }
        });

        if (commodityTypeNode) {
          gridApi?.ensureIndexVisible(
            commodityTypeNode.rowIndex ?? 0,
            "middle",
          );
          commodityTypeNode.setSelected(true);
          setCommodityType(commodityTypeNode.data || null);
          setShowModal("edit");
        }
      }
    }
  }, [gridApi]);

  useEffect(() => {
    registerConfig({
      id: "commodityTypeContext",
      getOptions: getMenuOptions,
    });
  }, []);

  const handleDeleteCommodityType = () => {
    if (!commodityType?.CommodityTypeId) return;

    deleteCommodityTypeMutation.mutate(commodityType.CommodityTypeId, {
      onSuccess: () => {
        setShowModal("");
        setCommodityType(null);
        if (gridApi) gridApi.deselectAll();
        navigate(BASE_PATH);
      },
      onError: (err) => {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to delete commodity type",
        );
      },
    });
  };

  return (
    <>
      <HelmetDexteel title={"Commodity Types"} />
      <Grid container justifyContent="center">
        <Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Commodity Types
          </Typography>
        </Grid>
        <Grid item md={12} xs={12} style={{ margin: "10px 18px" }}>
          <TextFieldSearchFilter
            value={filterValue}
            onChange={(val) => {
              setFilterValue(val);
              gridApi?.setGridOption("quickFilterText", val);
            }}
          />
        </Grid>
        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          style={{ width: "100%", padding: "0 10px" }}
          spacing={2}
        >
          <Grid item md={4} xs={12}>
            <div style={{ height: "75vh", width: "100%" }}>
              <AgGridReact<CommodityType>
                rowData={rows || []}
                columnDefs={columnDefs}
                defaultColDef={columnSimpleDefaults}
                rowHeight={34}
                headerHeight={34}
                animateRows
                rowSelection="single"
                getContextMenuItems={getContextMenuItems}
                onGridReady={(params) => setGridApi(params.api)}
                getRowId={(params) => String(params.data.CommodityTypeId)}
                loading={isLoading}
                onRowSelected={onRowSelected}
                gridOptions={{
                  statusBar: {
                    statusPanels: [
                      {
                        statusPanel: "agTotalRowCountComponent",
                        align: "right",
                      },
                      {
                        statusPanel: (params: IStatusPanelParams) => (
                          <AddCommodityTypeStatusPanel
                            handleNewCommodityType={handleNewCommodityType}
                          />
                        ),
                        align: "left",
                      },
                    ],
                  },
                }}
              />
            </div>
          </Grid>
          <Grid item md={8} xs={12}>
            <div style={{ height: "75vh", width: "100%" }}>
              <CommodityTypeDetails
                commodityType={commodityType}
                modal={showModal}
                onHide={onModalHide}
              />
            </div>
          </Grid>
        </Grid>
      </Grid>
      <MesfModal
        title={`DELETE COMMODITY TYPE`}
        open={showModal === "delete"}
        handleClose={() => setShowModal("")}
        maxWidth="md"
      >
        <MesfModal.Content style={{ padding: "15px 30px" }}>
          {isLoading && <CenteredLazyLoading />}
          {!isLoading && (
            <>
              <Grid container spacing={1}>
                <Grid item md={12} xs={12}>
                  <Typography>
                    Are you sure you want to delete{" "}
                    <strong>{commodityType?.CommodityTypeName}</strong>{" "}
                    commodity type?
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}
        </MesfModal.Content>
        <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item md={3} xs={12} style={{ margin: 0 }}>
              <Button
                fullWidth
                variant="contained"
                color="inherit"
                onClick={() => setShowModal("")}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item md={3} xs={12} style={{ margin: 0 }}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={handleDeleteCommodityType}
              >
                DELETE
              </Button>
            </Grid>
          </Grid>
        </MesfModal.Actions>
      </MesfModal>
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
